import { useCallback, useEffect, useRef, useState } from "react";
import { API, http } from "@/lib/constants";

// ---------- governance data-fetch hook ----------
// Thin wrapper over the shared `http` axios client (which already sends the
// internal project_id header). Handles: request de-duplication via a generation
// counter (stale responses can't overwrite newer state), a sane timeout for the
// occasionally-slow upstream API, and manual refetch. Each governance panel owns
// its own loading/error so a single failing endpoint never blanks the page.
export function useGov(path, { params, enabled = true, timeout = 20000 } = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const genRef = useRef(0);
  const paramsKey = JSON.stringify(params ?? null);

  const run = useCallback(async () => {
    const myGen = ++genRef.current;
    setLoading(true);
    setError(null);
    try {
      const res = await http.get(`${API}${path}`, { params, timeout });
      if (genRef.current !== myGen) return;
      setData(res.data);
      setError(null);
    } catch (e) {
      if (genRef.current !== myGen) return;
      const resp = e.response;
      const detail = resp?.data?.detail ?? resp?.data;
      const message = typeof detail === "string" ? detail : (detail?.message || detail?.error || e.message);
      setError({ message, status: resp?.status });
    } finally {
      if (genRef.current === myGen) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, paramsKey, timeout]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    run();
    return () => {
      // Invalidate any in-flight request on unmount / dependency change.
      genRef.current += 1;
    };
  }, [run, enabled]);

  return { data, error, loading, refetch: run };
}
